import { useState } from "react";
import { toast } from "sonner";
import { GripVertical, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { CATEGORY, colorCls, fmtAge } from "@/lib/format";
import { useNow } from "@/hooks/useNow";

function UnitCard({ unit, statuses, onOpenIncident, onAssignToSelected, selectedLive, now }) {
  const [busy, setBusy] = useState(false);
  const options = statuses.filter((s) => s.discipline === unit.discipline);
  const changeStatus = async (status_id) => {
    if (!status_id || status_id === unit.status.id) return;
    setBusy(true);
    try {
      const r = await api.post(`/units/${unit.id}/status`, { status_id, via: "board" });
      toast.success(`${unit.call_sign} → ${r.data.status}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div
      data-testid={`unit-card-${unit.call_sign}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/unit-id", unit.id); e.dataTransfer.setData("text/plain", unit.call_sign); e.dataTransfer.effectAllowed = "move"; }}
      className={`group rounded-lg border bg-background/60 px-2 py-1.5 flex items-center gap-2 cursor-grab active:cursor-grabbing border-l-4 ${colorCls(unit.status.color).split(" ").filter((c) => c.startsWith("border-")).join(" ")}`}
    >
      <GripVertical className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="mono text-base font-semibold leading-tight w-12 shrink-0" data-testid={`unit-callsign-${unit.call_sign}`}>{unit.call_sign}</span>
          <span data-testid={`unit-status-${unit.call_sign}`} className={`chip ${colorCls(unit.status.color)}`}>{unit.status.label}</span>
          <span className="mono text-[11px] text-muted-foreground tabular-nums" data-testid={`unit-elapsed-${unit.call_sign}`}>{fmtAge(unit.status_since, now)}</span>
          <span className="overline-label !text-[9px] truncate ml-auto" title={unit.personnel.map((p) => p.name).join(", ")}>
            {unit.personnel.length ? unit.personnel.map((p) => p.name.replace(" (SAMPLE)", "")).join(", ") : unit.discipline}
          </span>
        </div>
        {unit.incident ? (
          <button data-testid={`unit-incident-link-${unit.call_sign}`} onClick={() => onOpenIncident(unit.incident.id)} className="mt-0.5 text-left text-[11px] text-primary hover:underline truncate block max-w-full">
            <span className="mono font-semibold">{unit.incident.incident_number}</span> · {unit.incident.call_type_name} · {unit.incident.location_raw || "Location pending"}
          </button>
        ) : (
          <div className="mt-0.5 text-[11px] text-muted-foreground truncate">{unit.description}</div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {selectedLive && (!unit.incident || unit.incident.id !== selectedLive.id) && (
          <button data-testid={`unit-assign-selected-${unit.call_sign}`} onClick={() => onAssignToSelected(unit.id)} title={`Assign ${unit.call_sign} to ${selectedLive.incident_number}`}
            className="h-7 px-1.5 rounded-md border text-[11px] mono hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors flex items-center gap-0.5">
            <ArrowRight className="h-3 w-3" />{selectedLive.incident_number.slice(-3)}
          </button>
        )}
        <select data-testid={`unit-status-select-${unit.call_sign}`} value={unit.status.id || ""} disabled={busy} onChange={(e) => changeStatus(e.target.value)}
          className="h-7 rounded-md border bg-background text-[11px] px-1 max-w-[120px]">
          {options.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
    </div>
  );
}

export function UnitBoard({ units, statuses, onOpenIncident, onAssignToSelected, selectedLive }) {
  const now = useNow(1000);
  const groups = ["available", "committed", "out_of_service"].map((cat) => ({ cat, units: units.filter((u) => u.status.category === cat) }));
  return (
    <section className="pane h-full" data-testid="unit-board-pane">
      <div className="pane-head">
        <span className="overline-label">Unit Board</span>
        <span className="mono text-xs text-muted-foreground">{groups.map((g) => `${g.units.length} ${CATEGORY[g.cat].label.toLowerCase()}`).join(" · ")}</span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-2 space-y-3">
        {groups.map((g) => (
          <div key={g.cat} data-testid={`unit-group-${g.cat}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`h-2 w-2 rounded-sm ${CATEGORY[g.cat].bar}`} />
              <span className={`overline-label ${CATEGORY[g.cat].cls}`}>{CATEGORY[g.cat].label}</span>
              <span className="mono text-[11px] text-muted-foreground">{g.units.length}</span>
              <span className="flex-1 border-t border-dashed" />
            </div>
            {g.units.length === 0 ? <div className="text-[11px] text-muted-foreground pl-4">None</div> : (
              <div className="grid grid-cols-1 gap-1.5">
                {g.units.map((u) => <UnitCard key={u.id} unit={u} statuses={statuses} onOpenIncident={onOpenIncident} onAssignToSelected={onAssignToSelected} selectedLive={selectedLive} now={now} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
